import React, { useCallback, useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";

// region Imports - External libraries
import classNames from "classnames";
import Loading from "react-fullscreen-loading";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import DoneAllTwoToneIcon from "@material-ui/icons/DoneAllTwoTone";
// endregion Imports - External libraries
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  AlertMessages,
  DataTableMessages,
  GlobalMessages,
  ToastMessages
} from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared
import { AlertPopupDto } from "@shared/dtos/alerts/alert-popup.dto";
import { StandardReturnObject } from "@shared/utils/schemas/standard-return-object";
import { Alert } from "@shared/interfaces/alert.interface";
// endregion Imports - Shared
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Components
import NewDataTable, { DataTableActions, DataTableColumns } from "@molecules/NewDataTable";
import AlertDialogJustify from "@components/Alert/AlertDialogJustify";
import ButtonTable from "@components/Button/ButtonTable";
// endregion Imports - Components
// region Imports - Utils
import utils from "@utils/useful-functions";
// endregion Imports - Utils
// region Imports - Styles
import { Container } from "./styles";
// endregion Imports - Styles

interface AlertsModalProps {
  open: boolean;
  onClose: () => void;
  alertIds: string[];
  title?: string;
}

const AlertsModal: React.FC<AlertsModalProps> = ({
  open,
  onClose,
  alertIds,
  title
}) => {

  // region States
  const [loading, setLoading] = useState(false);
  const [alertsData, setAlertsData] = useState<AlertPopupDto[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<Alert>({} as Alert);
  const [actionType, setActionType] = useState<"details" | "update">("details");
  const [isLoadingSelectedAlert, setIsLoadingSelectedAlert] = useState(false);
  // endregion States

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks

  // region Constants
  const dataTableSettings: DataTables.Settings = {
    order: [[2, "desc"]],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };

  const dataTableActions: DataTableActions<AlertPopupDto>[] = [
    { ref: ".alert-justify", callback: (rowData) => { handleSelectAlert(rowData.id, "update"); } },
    { ref: ".alert-justified", callback: (rowData) => { handleSelectAlert(rowData.id, "details"); } }
  ];

  const dataTableColumns: DataTableColumns[] = [
    { // Veículo
      title: t(GlobalMessages.vehicle),
      data: (data: AlertPopupDto) => data.vehicle_code,
      defaultContent: "",
      filterable: true,
      propertyName: "vehicle_code"
    },
    { // Data da ocorrência
      title: t(AlertMessages.occurrenceDate),
      data: (data: AlertPopupDto) => utils.formatDateIfHave(data.occurrence_date, "fullDate"),
      filterable: false
    },
    { // Ações
      title: t(DataTableMessages.actions),
      orderable: false,
      searchable: false,
      data: (data: AlertPopupDto) => ReactDOMServer.renderToString(
        <Grid container spacing={1}>
          <Grid item xs sm md lg xl>
            <ButtonTable
              aria-disabled
              className={classNames(
                "action-button", data.is_justified ? "alert-justified" : "alert-justify"
              )}
            >
              {data.is_justified ? <SearchIcon /> : <DoneAllTwoToneIcon />}
            </ButtonTable>
          </Grid>
        </Grid>
      ),
      filterable: false
    }
  ];
  // endregion Constants

  // region Functions
  const fetchAlerts = useCallback(async () => {
    if (alertIds.length === 0) {
      setAlertsData([]);

      return;
    }

    setLoading(true);

    try {
      const chunkSize = 500;
      const chunks: string[][] = [];

      for (let i = 0; i < alertIds.length; i += chunkSize) {
        chunks.push(alertIds.slice(i, i + chunkSize));
      }

      const responses = await Promise.all(
        chunks.map((chunk) => api.post<StandardReturnObject<AlertPopupDto[]>>("/alerts/popup", { alertIds: chunk }))
      );

      const allAlerts: AlertPopupDto[] = [];
      let hasError = false;

      for (const { data } of responses) {
        if (data.status === "success") {
          allAlerts.push(...(data.result ?? []));
        } else {
          hasError = true;
          addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
          break;
        }
      }

      if (!hasError) {
        setAlertsData(allAlerts);
      }
    } catch (error: any) {
      if (!error.response) {
        addToast({
          type: "error",
          title: t(ToastMessages.error),
          description: t(ToastMessages.connectionNotEstablished)
        });
      } else {
        addToast({
          type: "error",
          title: error.response.data.backend ?? t(ToastMessages.error),
          description: error.response.data.message
        });
      }
    } finally {
      setLoading(false);
    }
  }, [alertIds, addToast, t]);

  const handleSelectAlert = async (alertId: string, type: "details" | "update") => {
    setIsLoadingSelectedAlert(true);
    setActionType(type);

    try {
      const response = await api.post(`alerts/read/${alertId}`);

      if (response.data.status === "success" && response.data.result?.[0]) {
        setSelectedAlert(response.data.result[0]);
      }
    } catch (error) {
      console.error("Error loading alert details:", error);
    } finally {
      setIsLoadingSelectedAlert(false);
    }
  };

  const closeCallbackJustifyAlert = useCallback((updatedData?: {
    authorizer_name: string;
    confirmation_date: Date;
    justification: string;
  }) => {
    if (updatedData && selectedAlert.id_alert) {
      setAlertsData((prevAlerts) =>
        prevAlerts.map((alert) =>
          alert.id === selectedAlert.id_alert
            ? { ...alert, is_justified: true }
            : alert
        )
      );
    }

    setSelectedAlert({} as Alert);
  }, [selectedAlert.id_alert]);
  // endregion Functions

  // region Effects
  useEffect(() => {
    if (open && alertIds.length > 0) {
      fetchAlerts();
    }
  }, [open, alertIds, fetchAlerts]);

  useEffect(() => {
    if (!open) {
      setAlertsData([]);
      setSelectedAlert({} as Alert);
    }
  }, [open]);
  // endregion Effects

  return (
    <>
      <Loading loading={loading || isLoadingSelectedAlert} />
      <Dialog
        open={open}
        onClose={onClose}
        scroll="paper"
        maxWidth="md"
        fullWidth
      >
        <Container>
          <DialogTitle className="mHeader">
            <div className="content">
              <span className="title">{title ?? t(AlertMessages.alert)}</span>
              <span className="subtitle">{alertsData.length} {t(AlertMessages.alert).toLowerCase()}(s)</span>
            </div>
          </DialogTitle>
          <DialogContent dividers className="mContent">
            <NewDataTable
              data={alertsData}
              columns={dataTableColumns}
              settings={dataTableSettings}
              actions={dataTableActions}
              showPaging={alertsData.length > 10}
              showInfo={alertsData.length > 10}
              showSearching={false}
            />
          </DialogContent>
          <DialogActions className="mFooter">
            <Button onClick={onClose} color="primary">
              {t(GlobalMessages.close)}
            </Button>
          </DialogActions>
        </Container>
      </Dialog>
      {selectedAlert.id_alert && (
        <AlertDialogJustify
          selectedAlert={selectedAlert}
          actionType={actionType}
          closeCallback={closeCallbackJustifyAlert}
        />
      )}
    </>
  );
};

AlertsModal.defaultProps = {
  title: undefined
};

export default AlertsModal;
